import React, { useState, useEffect } from "react";
import { Search, Filter, CheckCircle, XCircle, Eye } from "lucide-react";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";
import "./ProductApproval.css";
import Layout from "../component/Layout";

const ProductApproval = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [productToReject, setProductToReject] = useState(null);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch ALL products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        console.log("Fetching products from Firestore...");
        const productsRef = collection(db, "products");
        const q = query(productsRef, orderBy("timestamp", "desc"));

        const querySnapshot = await getDocs(q);
        const productsData = [];

        console.log(`Found ${querySnapshot.docs.length} products`);

        for (const docSnap of querySnapshot.docs) {
          const product = { id: docSnap.id, ...docSnap.data() };
          if (product.serviceType !== "openmarket") {
            productsData.push(product);
          }
        }
        console.log("Products data:", productsData);
        setProducts(productsData);
        setFilteredProducts(productsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products based on search term and status filter
  useEffect(() => {
    let filtered = products;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((product) => product.status === statusFilter);
    }

    // Apply search term filter
    if (searchTerm !== "") {
      filtered = filtered.filter(
        (product) =>
          product.productName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          product.segment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.status?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Calculate total pages
    setTotalPages(Math.max(1, Math.ceil(filtered.length / productsPerPage)));

    // Get products for current page
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const paginatedProducts = filtered.slice(startIndex, endIndex);

    setFilteredProducts(paginatedProducts);
  }, [
    searchTerm,
    statusFilter,
    products,
    currentPage,
    productsPerPage,
  ]);

  const handleApprove = async (productId) => {
    try {
      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, {
        status: "approved",
        approvedAt: new Date(),
      });

      // Update local state
      setProducts((prev) =>
        prev.map((product) =>
          product.id === productId
            ? { ...product, status: "approved", approvedAt: new Date() }
            : product,
        ),
      );
    } catch (error) {
      console.error("Error approving product:", error);
    }
  };

  const openRejectModal = (productId) => {
    setProductToReject(productId);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason("");
    setProductToReject(null);
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      return;
    }

    try {
      const productRef = doc(db, "products", productToReject);
      await updateDoc(productRef, {
        status: "rejected",
        rejectionReason: rejectReason,
        rejectedAt: new Date(),
      });

      // Update local state
      setProducts((prev) =>
        prev.map((product) =>
          product.id === productToReject
            ? {
                ...product,
                status: "rejected",
                rejectionReason: rejectReason,
                rejectedAt: new Date(),
              }
            : product,
        ),
      );

      closeRejectModal();
    } catch (error) {
      console.error("Error rejecting product:", error);
    }
  };

  const viewProductDetails = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="operator-product-approval">
        <div className="loading-container">Loading products...</div>
      </div>
    );
  }

  return (
    <Layout pageTitle="Product Approval">
      <div className="operator-product-approval">
        <div className="approval-header">
          <div className="header-content">
            <h1>Product Approval</h1>
            <div className="kpis-compact">
              <div className="kpi-compact">
                <h3>Total Products</h3>
                <p>{products.length}</p>
              </div>
              <div className="kpi-compact">
                <h3>Pending</h3>
                <p>{products.filter((p) => p.status === "pending").length}</p>
              </div>
              <div className="kpi-compact">
                <h3>Approved</h3>
                <p>{products.filter((p) => p.status === "approved").length}</p>
              </div>
              <div className="kpi-compact">
                <h3>Rejected</h3>
                <p>{products.filter((p) => p.status === "rejected").length}</p>
              </div>
              <div className="kpi-compact">
                <h3>Ready Service</h3>
                <p>
                  {products.filter((p) => p.serviceType === "ready").length}
                </p>
              </div>
              <div className="kpi-compact">
                <h3>Order Service</h3>
                <p>
                  {products.filter((p) => p.serviceType === "order").length}
                </p>
              </div>
              <div className="kpi-compact">
                <h3>Open Market</h3>
                <p>
                  {
                    products.filter((p) => p.serviceType === "openmarket")
                      .length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="controls">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <Filter size={20} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        <div className="products-container">
          <div className="products-grid">
            {filteredProducts.length === 0 ? (
              <div className="no-products">No products found</div>
            ) : (
              filteredProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    {product.images && product.images.length > 0 && (
                      <img
                        src={product.images[0].url}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/200x200?text=No+Image";
                        }}
                      />
                    )}
                    {(!product.images || product.images.length === 0) && (
                      <div className="no-image">No Image</div>
                    )}
                  </div>

                  <div className="product-info">
                    <div className="product-header">
                      <h3 className="product-name">
                        {product.productName || "N/A"}
                      </h3>
                      <div className="product-tags">
                        <span className={`status-badge ${product.status}`}>
                          {product.status}
                        </span>
                        <span
                          className={`service-type-badge ${product.serviceType}`}
                        >
                          {product.serviceType || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="product-meta">
                      <div className="meta-item">
                        <span className="meta-label">Segment:</span>
                        <span className="meta-value">
                          {product.segment || "N/A"}
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Category:</span>
                        <span className="meta-value">
                          {product.category || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="product-details">
                      <div className="detail-row">
                        <span className="detail-label">Gross:</span>
                        <span className="detail-value">
                          {product.grossWt ||
                            product.lotTotals?.totalGrossWt ||
                            0}
                          g
                        </span>
                        <span className="detail-label">Net:</span>
                        <span className="detail-value">
                          {product.netWt || product.lotTotals?.totalNetWt || 0}g
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Purity:</span>
                        <span className="detail-value">
                          {product.purity || "N/A"}
                        </span>
                        <span className="detail-label">Wastage:</span>
                        <span className="detail-value">
                          {product.wastage || 0}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="product-actions">
                    <button
                      onClick={() => viewProductDetails(product)}
                      className="view-btn"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>

                    {product.status === "pending" && (
                      <>
                        <button
                          onClick={() => handleApprove(product.id)}
                          className="approve-btn"
                          title="Approve"
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          onClick={() => openRejectModal(product.id)}
                          className="reject-btn"
                          title="Reject"
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {currentPage} of {totalPages} pages
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
              <div className="page-size-selector">
                <label>Items per page:</label>
                <select
                  value={productsPerPage}
                  onChange={(e) => {
                    setProductsPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Product Detail Modal */}
        {showModal && selectedProduct && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Product Details</h2>
                <button className="close-btn" onClick={closeModal}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="product-images">
                  {selectedProduct.images &&
                    selectedProduct.images.length > 0 && (
                      <>
                        <div className="product-main-image">
                          <img
                            src={selectedProduct.images[0].url}
                            alt={selectedProduct.productName}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/300x300?text=Image+Error";
                            }}
                          />
                        </div>
                        {selectedProduct.images.length > 1 && (
                          <div className="product-thumbnails">
                            {selectedProduct.images.map((image, index) => (
                              <img
                                key={index}
                                src={image.url}
                                alt={`Thumbnail ${index + 1}`}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/60x60?text=No+Image";
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  {(!selectedProduct.images ||
                    selectedProduct.images.length === 0) && (
                    <div className="no-images">No images available</div>
                  )}
                </div>

                <div className="details-grid">
                  <div className="detail-group">
                    <h3>Product Information</h3>
                    <div className="detail-item">
                      <span className="label">Name:</span>
                      <span className="value">
                        {selectedProduct.productName || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Segment:</span>
                      <span className="value">
                        {selectedProduct.segment || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Category:</span>
                      <span className="value">
                        {selectedProduct.category || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Style Type:</span>
                      <span className="value">
                        {selectedProduct.styleType || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Specification:</span>
                      <span className="value">
                        {selectedProduct.specification || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Design Code:</span>
                      <span className="value">
                        {selectedProduct.designCode || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Source:</span>
                      <span className="value">
                        {selectedProduct.productSource || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Seller ID:</span>
                      <span className="value">
                        {selectedProduct.sellerId || "N/A"}
                      </span>
                    </div>
                    {selectedProduct.sizes &&
                      selectedProduct.sizes.length > 0 && (
                        <div className="detail-item">
                          <span className="label">Sizes:</span>
                          <span className="value">
                            {selectedProduct.sizes.join(", ")}
                          </span>
                        </div>
                      )}
                  </div>

                  <div className="detail-group">
                    <h3>Technical Details</h3>
                    <div className="detail-item">
                      <span className="label">Gross Weight:</span>
                      <span className="value">
                        {selectedProduct.grossWt ||
                          selectedProduct.lotTotals?.totalGrossWt ||
                          0}
                        g
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Net Weight:</span>
                      <span className="value">
                        {selectedProduct.netWt ||
                          selectedProduct.lotTotals?.totalNetWt ||
                          0}
                        g
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Purity:</span>
                      <span className="value">
                        {selectedProduct.purity || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Wastage:</span>
                      <span className="value">
                        {selectedProduct.wastage || 0}%
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Specification Wt:</span>
                      <span className="value">
                        {selectedProduct.specificationWt || 0}g
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Net Gram Mc:</span>
                      <span className="value">
                        {selectedProduct.netGramMc || 0}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Set Mc:</span>
                      <span className="value">
                        {selectedProduct.setMc || 0}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Spec Gram Rate:</span>
                      <span className="value">
                        {selectedProduct.specificationGramRate || 0}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Spec MC:</span>
                      <span className="value">
                        {selectedProduct.specificationMC || 0}
                      </span>
                    </div>
                  </div>

                  <div className="detail-group">
                    <h3>Business Details</h3>
                    <div className="detail-item">
                      <span className="label">Service Type:</span>
                      <span className="value">
                        {selectedProduct.serviceType || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Payment Method:</span>
                      <span className="value">
                        {selectedProduct.paymentMethod || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Seller Payment:</span>
                      <span className="value">
                        {selectedProduct.sellerPayment || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">MOQ Set:</span>
                      <span className="value">
                        {selectedProduct.moqSet || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Lot:</span>
                      <span className="value">
                        {selectedProduct.Lot ? "Yes" : "No"}
                      </span>
                    </div>
                    {selectedProduct.serviceType === "ready" && (
                      <>
                        <div className="detail-item">
                          <span className="label">Instock Gram:</span>
                          <span className="value">
                            {selectedProduct.instockGram || "N/A"}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Instock Set:</span>
                          <span className="value">
                            {selectedProduct.instockSet || "N/A"}
                          </span>
                        </div>
                      </>
                    )}
                    {selectedProduct.lotTotals && (
                      <div className="detail-item">
                        <span className="label">Lot Totals:</span>
                        <span className="value">
                          Sets: {selectedProduct.lotTotals.totalSets} | Net:{" "}
                          {selectedProduct.lotTotals.totalNetWt}g | Gross:{" "}
                          {selectedProduct.lotTotals.totalGrossWt}g
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="detail-group">
                    <h3>Timestamps</h3>
                    <div className="detail-item">
                      <span className="label">Created:</span>
                      <span className="value">
                        {selectedProduct.timestamp?.toDate
                          ? selectedProduct.timestamp.toDate().toLocaleString()
                          : selectedProduct.timestamp || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Approved At:</span>
                      <span className="value">
                        {selectedProduct.approvedAt?.toDate
                          ? selectedProduct.approvedAt.toDate().toLocaleString()
                          : selectedProduct.approvedAt || "N/A"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className="value">
                        {selectedProduct.status || "N/A"}
                      </span>
                    </div>
                    {selectedProduct.rejectionReason && (
                      <div className="detail-item">
                        <span className="label">Rejection Reason:</span>
                        <span className="value rejection-text">
                          {selectedProduct.rejectionReason}
                        </span>
                      </div>
                    )}
                    {selectedProduct.rejectedAt?.toDate && (
                      <div className="detail-item">
                        <span className="label">Rejected At:</span>
                        <span className="value">
                          {selectedProduct.rejectedAt.toDate().toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedProduct.rejectedAt &&
                      !selectedProduct.rejectedAt?.toDate && (
                        <div className="detail-item">
                          <span className="label">Rejected At:</span>
                          <span className="value">
                            {selectedProduct.rejectedAt || "N/A"}
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                <div className="summary-section">
                  <h3>Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="summary-label">Total Net Weight</span>
                      <span className="summary-value">
                        {selectedProduct.netWt ||
                          selectedProduct.lotTotals?.totalNetWt ||
                          0}
                        g
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Gross Weight</span>
                      <span className="summary-value">
                        {selectedProduct.grossWt ||
                          selectedProduct.lotTotals?.totalGrossWt ||
                          0}
                        g
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Sets</span>
                      <span className="summary-value">
                        {selectedProduct.lotTotals?.totalSets ||
                          selectedProduct.moqSet ||
                          0}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Purity</span>
                      <span className="summary-value">
                        {selectedProduct.purity || "N/A"}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Wastage</span>
                      <span className="summary-value">
                        {selectedProduct.wastage || 0}%
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">MOQ Set</span>
                      <span className="summary-value">
                        {selectedProduct.moqSet || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedProduct.lotDetails &&
                  selectedProduct.lotDetails.length > 0 && (
                    <div className="lot-details-section">
                      <h3>Lot Details</h3>
                      <div className="lot-table-wrapper">
                        <table className="lot-table">
                          <thead>
                            <tr>
                              <th>Size</th>
                              <th>Set</th>
                              <th>Gross Wt</th>
                              <th>Net Wt</th>
                              <th>Avg Gross</th>
                              <th>Avg Net</th>
                              <th>Avg Spec Wt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedProduct.lotDetails.map((lot, index) => (
                              <tr key={index}>
                                <td>{lot.size || "N/A"}</td>
                                <td>{lot.set || lot.avgNetWt || 0}</td>
                                <td>{lot.grossWt || lot.avgGrossWt || 0}g</td>
                                <td>{lot.netWt || lot.avgNetWt || 0}g</td>
                                <td>{lot.avgGrossWt || 0}g</td>
                                <td>{lot.avgNetWt || 0}g</td>
                                <td>{lot.avgSpecWt || 0}g</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {selectedProduct.status === "pending" && (
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        handleApprove(selectedProduct.id);
                        closeModal();
                      }}
                      className="approve-btn large"
                    >
                      Approve Product
                    </button>
                    <button
                      onClick={() => {
                        openRejectModal(selectedProduct.id);
                        closeModal();
                      }}
                      className="reject-btn large"
                    >
                      Reject Product
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reject Reason Modal */}
        {showRejectModal && (
          <div className="modal-overlay" onClick={closeRejectModal}>
            <div
              className="modal-content reject-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Reject Product</h2>
                <button className="close-btn" onClick={closeRejectModal}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <p>Please provide a reason for rejecting this product:</p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows={4}
                  className="reject-reason-input"
                />
                <div className="modal-actions">
                  <button onClick={closeRejectModal} className="cancel-btn">
                    Cancel
                  </button>
                  <button onClick={handleReject} className="reject-btn large">
                    Confirm Rejection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductApproval;
